/**
 * Currency Service for habitué
 * Handles multi-currency support with live/cached exchange rates
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Supported currencies
export const CURRENCIES = {
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
};

// Default/fallback exchange rates (base: GBP)
const DEFAULT_RATES = {
  GBP: 1,
  EUR: 1.17,  // 1 GBP = 1.17 EUR
  USD: 1.27,  // 1 GBP = 1.27 USD
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('habitue-currency');
      if (stored && CURRENCIES[stored]) {
        return stored;
      }
      // Try to detect from browser locale
      const locale = navigator.language || 'en-GB';
      if (locale.includes('US')) return 'USD';
      if (locale.includes('FR') || locale.includes('DE') || locale.includes('ES') || locale.includes('IT')) return 'EUR';
    }
    return 'GBP';
  });

  const [rates, setRates] = useState(DEFAULT_RATES);
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch live exchange rates
  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/currency/rates`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setRates(data.rates);
        setRatesLastUpdated(new Date(data.updated_at));
        
        // Cache rates in localStorage
        localStorage.setItem('habitue-rates', JSON.stringify({
          rates: data.rates,
          updated_at: data.updated_at
        }));
      }
    } catch (error) {
      console.warn('[Currency] Failed to fetch rates, using cached/defaults:', error);
      
      // Try to load from cache
      const cached = localStorage.getItem('habitue-rates');
      if (cached) {
        const { rates: cachedRates, updated_at } = JSON.parse(cached);
        setRates(cachedRates);
        setRatesLastUpdated(new Date(updated_at));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch rates on mount and periodically
  useEffect(() => {
    fetchRates();
    
    // Refresh rates every 30 minutes
    const interval = setInterval(fetchRates, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchRates]);

  // Set currency with persistence
  const setCurrency = (code) => {
    if (CURRENCIES[code]) {
      setCurrencyState(code);
      localStorage.setItem('habitue-currency', code);
    }
  };

  // Convert amount from GBP to current currency
  const convert = useCallback((amountGBP, targetCurrency = currency) => {
    const rate = rates[targetCurrency] || 1;
    return amountGBP * rate;
  }, [rates, currency]);

  // Convert from any currency to GBP
  const toGBP = useCallback((amount, fromCurrency = currency) => {
    const rate = rates[fromCurrency] || 1;
    return amount / rate;
  }, [rates, currency]);

  // Format amount in current currency
  const format = useCallback((amountGBP, options = {}) => {
    const {
      showSymbol = true,
      decimals = 2,
      targetCurrency = currency
    } = options;

    const converted = convert(amountGBP, targetCurrency);
    const formatted = converted.toFixed(decimals);
    
    if (showSymbol) {
      const { symbol } = CURRENCIES[targetCurrency] || CURRENCIES.GBP;
      return `${symbol}${formatted}`;
    }
    
    return formatted;
  }, [convert, currency]);

  // Format with currency code (for clarity)
  const formatWithCode = useCallback((amountGBP, targetCurrency = currency) => {
    const converted = convert(amountGBP, targetCurrency);
    const { symbol, code } = CURRENCIES[targetCurrency] || CURRENCIES.GBP;
    return `${symbol}${converted.toFixed(2)} ${code}`;
  }, [convert, currency]);

  const value = {
    currency,
    currencyInfo: CURRENCIES[currency],
    currencies: CURRENCIES,
    rates,
    ratesLastUpdated,
    loading,
    setCurrency,
    convert,
    toGBP,
    format,
    formatWithCode,
    refreshRates: fetchRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Currency Selector Component
export const CurrencySelector = ({ className = '', showFlag = true, compact = false }) => {
  const { currency, setCurrency, currencies } = useCurrency();

  if (compact) {
    return (
      <select
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
        className={`bg-[#1E2F26] border border-[#283D32] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C3A365] ${className}`}
        data-testid="currency-selector-compact"
      >
        {Object.values(currencies).map((curr) => (
          <option key={curr.code} value={curr.code}>
            {showFlag ? `${curr.flag} ` : ''}{curr.code}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={`flex gap-1 bg-[#1E2F26] border border-[#283D32] rounded-lg p-1 ${className}`}>
      {Object.values(currencies).map((curr) => (
        <button
          key={curr.code}
          onClick={() => setCurrency(curr.code)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-all ${
            currency === curr.code
              ? 'bg-[#C3A365] text-[#0F1A14]'
              : 'text-[#8BA89A] hover:text-white'
          }`}
          data-testid={`currency-${curr.code}`}
        >
          {showFlag && <span>{curr.flag}</span>}
          <span className="font-medium">{curr.symbol}</span>
        </button>
      ))}
    </div>
  );
};

export default CurrencyContext;
